import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Divider,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Pagination,
  Stack,
  Tooltip,
  Menu,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  PersonAdd as PersonAddIcon,
  PersonRemove as PersonRemoveIcon,
  Security as SecurityIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import {
  getAllUsers,
  getUserById,
  updateUser,
  deleteUser,
  activateUser,
  deactivateUser,
  assignRolesToUser,
  addRoleToUser,
  removeRoleFromUser,
} from "../../api/userManagement";
import { createUser } from "../../api/userManagement";
import { getAllRoles } from "../../api/roles";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilter, setActiveFilter] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [sortBy, setSortBy] = useState("id");
  const [sortDirection, setSortDirection] = useState("asc");

  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [roleForm, setRoleForm] = useState({ roles: [] });
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [createForm, setCreateForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    password: "",
    countryCode: "",
    role: "USER",
  });

  // Menu states
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuUserId, setMenuUserId] = useState(null);

  useEffect(() => {
    loadUsers();
    loadRoles();
  }, [page, size, searchTerm, activeFilter, roleFilter, sortBy, sortDirection]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const params = {
        searchTerm: searchTerm || undefined,
        active: activeFilter || undefined,
        role: roleFilter || undefined,
        page,
        size,
        sortBy,
        sortDirection,
      };

      const data = await getAllUsers(params);
      setUsers(data.content || []);
      setTotalPages(data.totalPages || 0);
      setTotalElements(data.totalElements || 0);
    } catch (error) {
      console.error("Error loading users:", error);
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const loadRoles = async () => {
    try {
      const data = await getAllRoles();
      setRoles(data || []);
    } catch (error) {
      console.error("Error loading roles:", error);
    }
  };

  const handleSearch = () => {
    setPage(0);
    loadUsers();
  };

  const handleClearFilters = () => {
    setSearchTerm("");
    setActiveFilter("");
    setRoleFilter("");
    setPage(0);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setEditForm({
      firstName: user.firstName || "",
      lastName: user.lastName || "",
      email: user.email || "",
      phoneNumber: user.phoneNumber || "",
      countryCode: user.countryCode || "",
      active: user.active || false,
    });
    setEditDialogOpen(true);
  };

  const handleUpdateUser = async () => {
    try {
      await updateUser(selectedUser.id, editForm);
      toast.success("User updated successfully");
      setEditDialogOpen(false);
      loadUsers();
    } catch (error) {
      console.error("Error updating user:", error);
      toast.error("Failed to update user");
    }
  };

  const handleOpenConfirm = (userId) => {
    setSelectedUserId(userId);
    setOpenConfirm(true);
  };
  const handleDeleteUser = async () => {
    // if (window.confirm("Are you sure you want to delete this user?")) {
    try {
      await deleteUser(selectedUserId);
      toast.success("User deleted successfully");
      loadUsers();
    } catch (error) {
      console.error("Error deleting user:", error);
      toast.error("Failed to delete user");
    } finally {
      setOpenConfirm(false);
    }
  };

  const handleToggleUserStatus = async (userId, currentStatus) => {
    try {
      if (currentStatus) {
        await deactivateUser(userId);
        toast.success("User deactivated successfully");
      } else {
        await activateUser(userId);
        toast.success("User activated successfully");
      }
      loadUsers();
    } catch (error) {
      console.error("Error toggling user status:", error);
      toast.error("Failed to update user status");
    }
  };

  const handleManageRoles = (user) => {
    setSelectedUser(user);
    setRoleForm({ roles: user.roles || [] });
    setRoleDialogOpen(true);
  };

  const handleUpdateRoles = async () => {
    try {
      await assignRolesToUser(selectedUser.id, roleForm.roles);
      toast.success("User roles updated successfully");
      setRoleDialogOpen(false);
      loadUsers();
    } catch (error) {
      console.error("Error updating roles:", error);
      toast.error("Failed to update user roles");
    }
  };

  const handleMenuClick = (event, userId) => {
    setAnchorEl(event.currentTarget);
    setMenuUserId(userId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuUserId(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusChip = (active) => (
    <Chip
      label={active ? "Active" : "Inactive"}
      color={active ? "success" : "error"}
      size="small"
    />
  );

  // Define fixed colors for known roles
  const roleColors = {
    ADMIN: "#ffb3b3",
    USER: "#bbdefb",
    MODERATOR: "#c8e6c9",
    MARKETING: "#ffe0b2",
    OPS: "#e1bee7",
    EDITOR: "#b2ebf2",
    ALL_FUNCTIONS: "#d7ccc8",
  };

  // Helper: Generate a consistent color for unknown roles
  const stringToColor = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    const hue = Math.abs(hash % 360);
    return `hsl(${hue}, 60%, 50%)`;
  };

  const getRoleChips = (userRoles) => (
    <Stack
      direction="row"
      useFlexGap
      spacing={0.5}
      flexWrap="wrap"
      sx={{
        rowGap: 1,
        mt: 0.5,
        mb: 0.5,
      }}
    >
      {userRoles?.map((role, index) => {
        const color = roleColors[role] || stringToColor(role); // fallback for new roles
        return (
          <Chip
            key={index}
            label={role}
            size="small"
            sx={{
              backgroundColor: color,
              color: "#333",
              fontWeight: 550,
            }}
          />
        );
      })}
    </Stack>
  );

  return (
    <Box sx={{ p: { xs: 1, md: 2 }, width: { xs: "380px", md: "99%" } }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" }, // column on mobile, row on desktop
          justifyContent: { xs: "flex-start", md: "space-between" }, // start on mobile, space-between on desktop
          alignItems: { xs: "stretch", md: "center" }, // stretch on mobile, center on desktop
          gap: { xs: 1, md: 0 }, // spacing between items
          mb: 2,
        }}
      >
        <Typography variant="h4" sx={{ fontSize: "22px" }} component="h1">
          User Management
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            gap: 1,
            flexWrap: { xs: "wrap", md: "nowrap" }, // wrap buttons on mobile
            mt: { xs: 1, md: 0 },
          }}
        >
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={loadUsers}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            startIcon={<PersonAddIcon />}
            onClick={() => setCreateDialogOpen(true)}
            sx={{ ml: 1 }}
          >
            Create User
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                size="small"
                fullWidth
                label="Search Users"
                placeholder="Name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <IconButton onClick={handleSearch}>
                      <SearchIcon />
                    </IconButton>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  sx={{ textAlign: "left" }}
                  value={activeFilter}
                  onChange={(e) => setActiveFilter(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="true">Active</MenuItem>
                  <MenuItem value="false">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Role</InputLabel>
                <Select
                  value={roleFilter}
                  sx={{ textAlign: "left" }}
                  onChange={(e) => setRoleFilter(e.target.value)}
                  label="Role"
                >
                  <MenuItem value="">All</MenuItem>
                  {roles.map((role) => (
                    <MenuItem key={role.name} value={role.name}>
                      {role.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortBy}
                  sx={{ textAlign: "left" }}
                  onChange={(e) => setSortBy(e.target.value)}
                  label="Sort By"
                >
                  <MenuItem value="id">ID</MenuItem>
                  <MenuItem value="firstName">First Name</MenuItem>
                  <MenuItem value="lastName">Last Name</MenuItem>
                  <MenuItem value="email">Email</MenuItem>
                  <MenuItem value="dateJoined">Date Joined</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Direction</InputLabel>
                <Select
                  value={sortDirection}
                  sx={{ textAlign: "left" }}
                  onChange={(e) => setSortDirection(e.target.value)}
                  label="Direction"
                >
                  <MenuItem value="asc">Ascending</MenuItem>
                  <MenuItem value="desc">Descending</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={1}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleClearFilters}
                // startIcon={<FilterIcon />}
              >
                Clear
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <TableContainer sx={{ maxHeight: "70vh" }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    backgroundColor: "#17479e",
                    color: "white",
                    fontWeight: 600,
                    position: "sticky",
                    top: 0,
                    zIndex: 3,
                  }}
                >
                  ID
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: "#17479e",
                    color: "white",
                    fontWeight: 600,
                    position: "sticky",
                    top: 0,
                    zIndex: 3,
                  }}
                >
                  Name
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: "#17479e",
                    color: "white",
                    fontWeight: 600,
                    position: "sticky",
                    top: 0,
                    zIndex: 3,
                  }}
                >
                  Email
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: "#17479e",
                    color: "white",
                    fontWeight: 600,
                    position: "sticky",
                    top: 0,
                    zIndex: 3,
                  }}
                >
                  Phone
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: "#17479e",
                    color: "white",
                    fontWeight: 600,
                    position: "sticky",
                    top: 0,
                    zIndex: 3,
                  }}
                >
                  Status
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: "#17479e",
                    color: "white",
                    fontWeight: 600,
                    position: "sticky",
                    top: 0,
                    zIndex: 3,
                  }}
                >
                  Roles
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: "#17479e",
                    color: "white",
                    fontWeight: 600,
                    position: "sticky",
                    top: 0,
                    zIndex: 3,
                  }}
                >
                  Date Joined
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: "#17479e",
                    color: "white",
                    fontWeight: 600,
                    position: "sticky",
                    top: 0,
                    right: 0,
                    zIndex: 4,
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="textSecondary">
                      No users found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>{user.id}</TableCell>
                    <TableCell>
                      {user.firstName} {user.lastName}
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.phoneNumber || "N/A"}</TableCell>
                    <TableCell>{getStatusChip(user.active)}</TableCell>
                    <TableCell>{getRoleChips(user.roles)}</TableCell>
                    <TableCell>{formatDate(user.dateJoined)}</TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        py: 1.2,
                        verticalAlign: "middle",
                        position: "sticky",
                        right: 0,
                        backgroundColor: "#fff",
                        zIndex: 1,
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={1.5}
                        justifyContent="center"
                        alignItems="center"
                      >
                        <Tooltip title="Edit User">
                          <IconButton
                            size="small"
                            onClick={() => handleEditUser(user)}
                            sx={{
                              backgroundColor: "#E3F2FD",
                              color: "#1976d2",
                              transition: "all 0.3s ease",
                              "&:hover": {
                                backgroundColor: "#1976d2",
                                color: "#fff",
                                transform: "scale(1.1)",
                              },
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Manage Roles">
                          <IconButton
                            size="small"
                            onClick={() => handleManageRoles(user)}
                            sx={{
                              backgroundColor: "#FFF3E0",
                              color: "#FB8C00",
                              transition: "all 0.3s ease",
                              "&:hover": {
                                backgroundColor: "#FB8C00",
                                color: "#fff",
                                transform: "scale(1.1)",
                              },
                            }}
                          >
                            <SecurityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip
                          title={
                            user.active ? "Deactivate User" : "Activate User"
                          }
                        >
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleToggleUserStatus(user.id, user.active)
                            }
                            sx={{
                              backgroundColor: user.active
                                ? "#FFEBEE"
                                : "#E8F5E9",
                              color: user.active ? "#E53935" : "#43A047",
                              transition: "all 0.3s ease",
                              "&:hover": {
                                backgroundColor: user.active
                                  ? "#E53935"
                                  : "#43A047",
                                color: "#fff",
                                transform: "scale(1.1)",
                              },
                            }}
                          >
                            {user.active ? (
                              <PersonRemoveIcon fontSize="small" />
                            ) : (
                              <PersonAddIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>

                        {/* <Tooltip title="Delete User">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenConfirm(user.id)}
                            sx={{
                              backgroundColor: "#FFEBEE",
                              color: "#E53935",
                              transition: "all 0.3s ease",
                              "&:hover": {
                                backgroundColor: "#E53935",
                                color: "#fff",
                                transform: "scale(1.1)",
                              },
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip> */}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {totalPages > 1 && (
          <Box display="flex" justifyContent="center" p={2}>
            <Pagination
              count={totalPages}
              page={page + 1}
              onChange={(event, value) => setPage(value - 1)}
              color="primary"
            />
          </Box>
        )}
      </Card>

      {/* Edit User Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <TextField
                size="small"
                fullWidth
                label="First Name"
                value={editForm.firstName}
                onChange={(e) =>
                  setEditForm({ ...editForm, firstName: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                size="small"
                fullWidth
                label="Last Name"
                value={editForm.lastName}
                onChange={(e) =>
                  setEditForm({ ...editForm, lastName: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Email"
                type="email"
                value={editForm.email}
                onChange={(e) =>
                  setEditForm({ ...editForm, email: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={8}>
              <TextField
                fullWidth
                size="small"
                label="Phone Number"
                value={editForm.phoneNumber}
                onChange={(e) =>
                  setEditForm({ ...editForm, phoneNumber: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                size="small"
                label="Country Code"
                value={editForm.countryCode}
                onChange={(e) =>
                  setEditForm({ ...editForm, countryCode: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={editForm.active}
                    onChange={(e) =>
                      setEditForm({ ...editForm, active: e.target.checked })
                    }
                  />
                }
                label="Active User"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdateUser} variant="contained">
            Update User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manage Roles Dialog */}
      <Dialog
        open={roleDialogOpen}
        onClose={() => setRoleDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Manage User Roles</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            User: {selectedUser?.firstName} {selectedUser?.lastName}
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel>Roles</InputLabel>
            <Select
              multiple
              value={roleForm.roles}
              onChange={(e) =>
                setRoleForm({ ...roleForm, roles: e.target.value })
              }
              label="Roles"
              renderValue={(selected) => (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {selected.map((value) => {
                    const bgColor =
                      roleColors[value.toUpperCase()] || "#e0e0e0"; // default gray if unknown role
                    return (
                      <Chip
                        key={value}
                        label={value}
                        size="small"
                        sx={{
                          backgroundColor: bgColor,
                          color: "#000",
                          fontWeight: 500,
                          borderRadius: "8px",
                          px: 0.5,
                          mb: 0.5,
                          "& .MuiChip-label": {
                            whiteSpace: "nowrap",
                          },
                        }}
                      />
                    );
                  })}
                </Box>
              )}
            >
              {roles.map((role) => (
                <MenuItem key={role.name} value={role.name}>
                  {role.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoleDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdateRoles} variant="contained">
            Update Roles
          </Button>
        </DialogActions>
      </Dialog>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() => {
            const user = users.find((u) => u.id === menuUserId);
            if (user) {
              handleDeleteUser(user.id);
            }
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete User</ListItemText>
        </MenuItem>
      </Menu>

      {/* Create User Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create User</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <TextField
                size="small"
                fullWidth
                label="First Name"
                value={createForm.firstName}
                onChange={(e) =>
                  setCreateForm({ ...createForm, firstName: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                size="small"
                fullWidth
                label="Last Name"
                value={createForm.lastName}
                onChange={(e) =>
                  setCreateForm({ ...createForm, lastName: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                size="small"
                fullWidth
                label="Email"
                type="email"
                value={createForm.email}
                onChange={(e) =>
                  setCreateForm({ ...createForm, email: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Password"
                type="password"
                value={createForm.password}
                onChange={(e) =>
                  setCreateForm({ ...createForm, password: e.target.value })
                }
                helperText="Minimum 8 characters"
                autoComplete="new-password"
              />
            </Grid>
            <Grid item xs={8}>
              <TextField
                fullWidth
                size="small"
                label="Phone Number"
                value={createForm.phoneNumber}
                onChange={(e) =>
                  setCreateForm({ ...createForm, phoneNumber: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                size="small"
                label="Country Code"
                value={createForm.countryCode}
                onChange={(e) =>
                  setCreateForm({ ...createForm, countryCode: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Role</InputLabel>
                <Select
                  value={createForm.role}
                  onChange={(e) =>
                    setCreateForm({ ...createForm, role: e.target.value })
                  }
                  label="Role"
                >
                  {roles.map((r) => (
                    <MenuItem key={r.name} value={r.name}>
                      {r.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={async () => {
              try {
                // Build DTO aligning to backend RegisterRequest
                const payload = {
                  firstName: createForm.firstName,
                  lastName: createForm.lastName || "",
                  email: createForm.email || "",
                  phoneNumber: createForm.phoneNumber || "",
                  password: createForm.password || "",
                  countryCode: createForm.countryCode || "",
                  role: createForm.role || "USER",
                };
                if (!payload.firstName || payload.firstName.length < 2)
                  throw new Error("First name min 2 chars");
                if (!payload.email) throw new Error("Email is required");
                if (!payload.phoneNumber)
                  throw new Error("Phone number is required");
                if (!payload.password || payload.password.length < 8)
                  throw new Error("Password min 8 chars");
                await createUser(payload);
                toast.success("User created successfully");
                setCreateDialogOpen(false);
                setCreateForm({
                  firstName: "",
                  lastName: "",
                  email: "",
                  phoneNumber: "",
                  password: "",
                  countryCode: "",
                  role: "USER",
                });
                loadUsers();
              } catch (error) {
                console.error("Error creating user:", error);
                const msg =
                  error?.response?.data?.message || "Failed to create user";
                toast.error(msg);
              }
            }}
            variant="contained"
          >
            Create User
          </Button>
        </DialogActions>
      </Dialog>
      {/* Modern Confirm Delete Dialog */}
      <Dialog
        open={openConfirm}
        onClose={() => setOpenConfirm(false)}
        aria-labelledby="confirm-dialog"
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 2,
            minWidth: 320,
          },
        }}
      >
        <DialogTitle
          id="confirm-dialog"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            color: "warning.main",
          }}
        >
          <WarningAmberIcon />
          Confirm Deletion
        </DialogTitle>

        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to delete this user? This action cannot be
            undone.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ justifyContent: "flex-end", p: 2 }}>
          <Button
            onClick={() => setOpenConfirm(false)}
            sx={{
              color: "#616161",
              borderRadius: 2,
              "&:hover": { backgroundColor: "#F5F5F5" },
            }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteUser}
            sx={{
              borderRadius: 2,
              px: 3,
              fontWeight: 600,
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement;
